<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { onBeforeRouteLeave } from 'vue-router'

import HomePageFooter from '../components/HomePageFooter.vue'
import HomePageHeader from '../components/HomePageHeader.vue'
import LandingBackground from '../components/LandingBackground.vue'
import MessageDialog from '../components/MessageDialog.vue'
import TaskStatusTag from '../components/TaskStatusTag.vue'
import TaskSubmitProgressDialog from '../components/TaskSubmitProgressDialog.vue'
import IconClose from '../components/icons/IconClose.vue'
import IconChevronLeft from '../components/icons/IconChevronLeft.vue'
import IconChevronRight from '../components/icons/IconChevronRight.vue'
import IconDownload from '../components/icons/IconDownload.vue'
import IconBrush from '../components/icons/IconBrush.vue'
import IconEraser from '../components/icons/IconEraser.vue'
import IconPlay from '../components/icons/IconPlay.vue'
import IconPause from '../components/icons/IconPause.vue'
import IconVolume from '../components/icons/IconVolume.vue'
import IconVolumeOff from '../components/icons/IconVolumeOff.vue'
import IconRefresh from '../components/icons/IconRefresh.vue'
import IconTrash from '../components/icons/IconTrash.vue'
import { brand } from '../config/brand'
import { apiRequest, assertOk, isAuthRequiredError, AUTH_EXPIRED_EVENT } from '../lib/apiClient'
import type { LandingSectionKey } from '../lib/landingTypes'
import { absoluteUrl, organizationJsonLd, webApplicationJsonLd } from '../lib/structuredData'
import { estimateTaskMinutes } from '../lib/taskEstimate'
import { TASK_STATUS, normalizeTaskStatus } from '../lib/taskStatus'
import { getUploadUrl, putFile } from '../lib/upload'
import { normalizePngMaskBase64 } from '../lib/maskEncoding'
import { trackEvent } from '../lib/track'
import { getBatchCreditsStandardText } from '../lib/taskCredits'
import { getTaskThumbFromCache, removeTaskThumbCache, setTaskThumbToCache } from '../lib/taskThumbCache'
import TimeRangeSeekBar from '../components/TimeRangeSeekBar.vue'
import { useAuthStore } from '../stores/auth'
import { useLoginModalStore } from '../stores/loginModal'
import { usePaymentModalStore } from '../stores/paymentModal'
import { useTaskStore } from '../stores/task'
import { useVideoTaskListStore } from '../stores/videoTasks'
import { TASK_TYPES } from '../workbench/taskCatalog'

type StartTaskResponse = { task_id?: string; status?: string; credits_required?: number }
type VideoEraseResponse = { task_id?: string; status?: string; credits_required?: number }
type LandingRecommendation = { icon: string; name: string; desc: string; href: string }

const props = withDefaults(
  defineProps<{
    embed?: boolean
    landingPageId?: string
    landingSeo?: any
    canonicalPath?: string
    breadcrumbName?: string
    howToTitle?: string
    seoTitle?: string
    seoDescription?: string
    useCasesImages?: string[]
    benefitsImages?: string[]
    featuresImages?: string[]
    whyChooseImages?: string[]
    enabledSections?: Partial<Record<LandingSectionKey, boolean>>
    demoVideoUrl?: string
    defaultInvitationCode?: string
    recommendations?: LandingRecommendation[]
    hreflang?: Record<string, string>
  }>(),
  {
    embed: false,
    landingPageId: '',
    landingSeo: undefined,
    canonicalPath: '/video-object-removal-app/',
    breadcrumbName: '视频消除笔',
    howToTitle: '如何使用视频消除笔',
    seoTitle: '',
    seoDescription: '',
    useCasesImages: () => [],
    benefitsImages: () => [],
    featuresImages: () => [],
    whyChooseImages: () => [],
    enabledSections: () => ({}),
    demoVideoUrl: 'https://7375-suyoo-4gfus2th28bc15ad-1327486388.cos.ap-shanghai.myqcloud.com/demo/video-object-removal-demo.mp4',
    defaultInvitationCode: '',
    recommendations: () => [],
    hreflang: () => ({}),
  },
)

const auth = useAuthStore()
const taskStore = useTaskStore()
const videoTasks = useVideoTaskListStore()
const loginModal = useLoginModalStore()
const paymentModal = usePaymentModalStore()

const filePicker = ref<HTMLInputElement | null>(null)
const videoEl = ref<HTMLVideoElement | null>(null)
const previewWrapEl = ref<HTMLElement | null>(null)
const maskCanvas = ref<HTMLCanvasElement | null>(null)

const videoFile = ref<File | null>(null)
const videoUrl = ref('')
const demoPlayed = ref(false)
const isMobileViewport = ref(false)
const videoW = ref(0)
const videoH = ref(0)
const videoDuration = ref(0)
const originObjectKey = ref('')
const uploadBusy = ref(false)

const toolMode = ref<'brush' | 'eraser'>('brush')
const brushSize = ref(48)
const maskTime = ref<number | null>(null)
const maskHasContent = ref(false)
const timeStart = ref(0)
const timeStop = ref(0)
const seekTime = ref(0)
const seekDragging = ref(false)
const eraseStrategy = ref<'fixed' | 'track'>('fixed')

const drawing = ref(false)
const lastPt = ref<{ x: number; y: number } | null>(null)
const outOfBounds = ref(false)
let maskResizeObs: ResizeObserver | null = null

const brushCursorVisible = ref(false)
const brushCursorX = ref(0)
const brushCursorY = ref(0)
const brushCursorScale = ref(1)

const playerPlaying = ref(false)
const playerMuted = ref(true)
const playerLastVolume = ref(1)
let seekRaf = 0
let taskPollTimer = 0
let taskPollBusy = false

const maskDisplayActive = computed(() => {
  if (!videoFile.value) return false
  if (!hasPreviewVideo.value) return false
  if (drawing.value) return true
  if (eraseStrategy.value === 'fixed') return maskHasContent.value
  const raw = maskTime.value
  if (raw === null || !Number.isFinite(raw)) return false
  const mt = Number(raw)
  const ct = Number(seekTime.value || 0)
  return Math.abs(ct - mt) <= 0.1
})

const hasPreviewVideo = computed(() => Boolean(videoEl.value) && Boolean(videoUrl.value))
const isDemoVideo = computed(() => !videoFile.value && !!videoUrl.value && videoUrl.value === String(props.demoVideoUrl || '').trim())
function syncMobileViewport() {
  const w = Number(window.innerWidth || 0) || 0
  isMobileViewport.value = w > 0 && w < 640
}
const brushCursorDiameter = computed(() => {
  const lineW = Math.max(4, Math.round(Number(brushSize.value || 0)) || 28)
  const scale = Math.max(0.0001, Number(brushCursorScale.value || 1))
  const css = lineW / scale
  return Math.max(8, Math.min(120, css))
})

const defaultLandingSeo = {
  h1: '视频消除笔',
  tagline: '涂抹选择消除对象，AI 自动跟踪并无痕修复画面',
  howtoTitle: props.howToTitle,
  howto: [],
  whereTitle: '',
  whereItems: [],
  featuresTitle: '',
  featuresItems: [],
  whyNeedTitle: '',
  whyNeedItems: [],
  whyTitle: '',
  whyItems: [],
  quoteTitle: '',
  quoteItems: [],
  faqTitle: '常见问题 (FAQ)',
  faq: [],
}

const landingSeo = computed(() => (props.landingSeo ? props.landingSeo : defaultLandingSeo))

const heroSubText = computed(() => {
  const tagline = String((props.landingSeo as any)?.tagline || '').trim()
  if (tagline) return tagline
  return ''
})

function whyNeedImageSrc(idx: number) {
  const list = Array.isArray(props.benefitsImages) ? props.benefitsImages : []
  const i = Math.max(0, Math.floor(Number(idx || 0)))
  const pick = list.length ? String(list[i] || list[i % list.length] || '').trim() : ''
  if (pick) return withBase(pick)
  const j = (Math.abs(Number(idx || 0)) % 2) + 1
  return withBase(`/assets/why-need-${j}.jpg`)
}

function whereImageSrc(idx: number) {
  const list = Array.isArray(props.useCasesImages) ? props.useCasesImages : []
  const i = Math.max(0, Math.floor(Number(idx || 0)))
  const pick = list.length ? String(list[i] || list[i % list.length] || '').trim() : ''
  if (pick) return withBase(pick)
  const j = Math.min(12, Math.max(1, i + 1))
  return withBase(`/assets/where-${j}.jpg`)
}

const whyChooseItems = computed(() => {
  const list = Array.isArray(props.whyChooseImages) ? props.whyChooseImages : []
  const items = Array.isArray((landingSeo.value as any)?.whyItems) ? ((landingSeo.value as any).whyItems as any[]) : []
  return items.map((w: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return w
    return { ...w, image: img }
  })
})

const featureItems = computed(() => {
  const list = Array.isArray(props.featuresImages) ? props.featuresImages : []
  const items = Array.isArray((landingSeo.value as any)?.featuresItems) ? ((landingSeo.value as any).featuresItems as any[]) : []
  return items.map((f: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return f
    return { ...f, image: img }
  })
})

const siteUrl = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  'https://www.wuhenai.com'
).replace(/\/+$/, '')
const canonical = computed(() => {
  const p = String(props.canonicalPath || '').trim()
  const path = p && p.startsWith('/') ? p : `/${p || ''}`
  return `${siteUrl}${path.replace(/\/?$/, '/')}`
})

function sectionEnabled(k: LandingSectionKey) {
  const m = props.enabledSections || {}
  if (typeof (m as any)[k] === 'boolean') return Boolean((m as any)[k])
  return true
}

const htmlLang = computed(() => {
  const m = props.hreflang || {}
  const k = Object.keys(m).find((x) => x && x !== 'x-default') || ''
  return k || 'zh-CN'
})

const isPrimaryLanding = computed(() => {
  if (props.embed) return false
  const id = String(props.landingPageId || '').trim()
  return id === 'video-object-removal'
})

const hreflangLinks = computed(() => {
  const m = props.hreflang || {}
  const out: Array<{ rel: string; hreflang?: string; href: string }> = []
  for (const [lang, path] of Object.entries(m)) {
    const p = String(path || '').trim()
    if (!p) continue
    const href = /^https?:\/\//.test(p) ? p : `${siteUrl}${p.startsWith('/') ? p : `/${p}`}`
    out.push({ rel: 'alternate', hreflang: lang, href })
  }
  return out
})

const description = computed(() => {
  const preset = String(props.seoDescription || '').trim()
  if (preset) return preset
  const steps = landingSeo.value.howto || []
  if (steps.length >= 2) return `${steps[0].desc} ${steps[1].desc}`.trim()
  return '上传视频，在画面上涂抹选择需要消除的对象，提交处理后即可下载无痕修复的视频。'
})

const pageTitle = computed(() => {
  const preset = String(props.seoTitle || '').trim()
  if (preset) return preset
  return landingSeo.value.h1 ? `${landingSeo.value.h1} - ${brand.productName}` : brand.productName
})

const socialImage = computed(() => absoluteUrl(siteUrl, brand.ogImagePath))

const faqJsonLd = computed(() => {
  if (!sectionEnabled('faq')) return ''
  const faq = Array.isArray(landingSeo.value.faq) ? landingSeo.value.faq : []
  if (!faq.length) return ''
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((x: any) => ({
      '@type': 'Question',
      name: String(x.question || ''),
      acceptedAnswer: { '@type': 'Answer', text: String(x.answer || '') },
    })),
  })
})

const orgJsonLd = computed(() => {
  return organizationJsonLd(siteUrl, absoluteUrl(siteUrl, brand.logoPath))
})

const webAppJsonLd = computed(() => {
  if (!isPrimaryLanding.value) return ''
  return webApplicationJsonLd({
    siteUrl,
    name: '无痕AI视频消除笔',
    description: `${brand.productName}是一款智能图片及视频批处理平台，支持视频消除、去水印等能力。`,
    inLanguage: htmlLang.value,
  })
})

const webPageJsonLd = computed(() => {
  const out: any = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical.value}#webpage`,
    url: canonical.value,
    name: pageTitle.value,
    description: description.value,
    inLanguage: htmlLang.value,
    isPartOf: { '@id': `${siteUrl}#website` },
  }
  if (isPrimaryLanding.value) out.about = { '@id': `${siteUrl}#webapp` }
  return JSON.stringify(out)
})

const breadcrumbJsonLd = computed(() => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: brand.productName, item: siteUrl },
      { '@type': 'ListItem', position: 2, name: props.breadcrumbName || '视频消除笔', item: canonical.value },
    ],
  })
})

useHead(() => {
  const links = [
    { rel: 'canonical', href: canonical.value },
    ...hreflangLinks.value,
  ] as any[]
  const scripts: any[] = []
  if (isPrimaryLanding.value) {
    scripts.push({ type: 'application/ld+json', textContent: orgJsonLd.value })
    scripts.push({ type: 'application/ld+json', textContent: webAppJsonLd.value })
  }
  scripts.push({ type: 'application/ld+json', textContent: webPageJsonLd.value })
  scripts.push({ type: 'application/ld+json', textContent: breadcrumbJsonLd.value })
  if (isPrimaryLanding.value && faqJsonLd.value) scripts.push({ type: 'application/ld+json', textContent: faqJsonLd.value })
  return {
    htmlAttrs: { lang: htmlLang.value },
    title: pageTitle.value,
    meta: [
      { name: 'description', content: description.value },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: pageTitle.value },
      { property: 'og:description', content: description.value },
      { property: 'og:url', content: canonical.value },
      { property: 'og:image', content: socialImage.value },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle.value },
      { name: 'twitter:description', content: description.value },
      { name: 'twitter:image', content: socialImage.value },
    ],
    link: links,
    script: scripts,
  }
})

const modalOpen = ref(false)
const modalTitle = ref('提示')
const modalPrimaryText = ref('我知道了')
const modalBodyHtml = ref('')
let modalAction: (() => void) | null = null

const warnOpen = ref(false)
const warnText = ref('')
function openWarn(msg: string) {
  warnText.value = String(msg || '').trim()
  warnOpen.value = true
}
function closeWarn() {
  warnOpen.value = false
  warnText.value = ''
}

function openModal(title: string, primaryText: string, bodyHtml: string, action?: () => void) {
  modalTitle.value = String(title || '提示')
  modalPrimaryText.value = String(primaryText || '我知道了')
  modalBodyHtml.value = String(bodyHtml || '')
  modalAction = action || null
  modalOpen.value = true
}

function closeModal() {
  modalOpen.value = false
}

function onModalPrimary() {
  const cb = modalAction
  modalAction = null
  modalOpen.value = false
  try {
    if (cb) cb()
  } catch {}
}

function withBase(p: string) {
  const s = String(p || '').trim()
  if (!s) return ''
  if (/^https?:\/\//.test(s)) return s
  const base = ((import.meta.env.BASE_URL as string | undefined) || '/').replace(/\/?$/, '/')
  return `${base}${s.replace(/^\//, '')}`
}

const tutorialIconMaskStyle = computed(() => {
  const href = withBase('/assets/tutorial-icon.svg')
  return {
    backgroundColor: 'currentColor',
    WebkitMask: `url("${href}") no-repeat center / contain`,
    mask: `url("${href}") no-repeat center / contain`,
  } as any
})

function resolveRecommendHref(href: string) {
  const s = String(href || '').trim()
  if (!s) return ''
  if (s.startsWith('#')) return s
  if (/^https?:\/\//.test(s)) return s
  const path = s.startsWith('/') ? s : `/${s}`
  const base = ((import.meta.env.BASE_URL as string | undefined) || '/').replace(/\/?$/, '/')
  if (base !== '/') return `${siteUrl}${path}`
  return path
}

function defaultVideoThumbDataUrl() {
  return withBase('/assets/default-video-thumb.svg')
}

function taskThumbSrc(t: any) {
  const id = String(t?.task_id || '').trim()
  const cached = getTaskThumbFromCache(id)
  if (cached) return cached
  return defaultVideoThumbDataUrl()
}

function ensureLoggedIn() {
  if (auth.isLoggedIn && auth.token) return true
  const nextPath = props.embed ? window.location.href : withBase(props.canonicalPath || '/video-object-removal-app/')
  loginModal.show({ tab: 'sms', nextPath, defaultInvitationCode: props.defaultInvitationCode || '' })
  return false
}

function onAuthExpired() {
  closeSubmitDialog()
  try {
    auth.clearToken()
  } catch {}
  ensureLoggedIn()
}

const submitOpen = ref(false)
const submitStage = ref<'uploading' | 'creating' | 'starting' | 'error'>('uploading')
const submitUploadPct = ref(0)
const submitErrorStep = ref<'upload' | 'create' | 'start'>('upload')
const submitErrorText = ref('')

function openSubmitDialog() {
  submitStage.value = 'uploading'
  submitUploadPct.value = 0
  submitErrorStep.value = 'upload'
  submitErrorText.value = ''
  submitOpen.value = true
}

function closeSubmitDialog() {
  submitOpen.value = false
}

function setSubmitError(step: 'upload' | 'create' | 'start', text: string) {
  submitErrorStep.value = step
  submitErrorText.value = String(text || '提交失败')
  submitStage.value = 'error'
}

function validateVideoMeta() {
  const dur = Number(videoDuration.value || 0)
  const w = Number(videoW.value || 0)
  const h = Number(videoH.value || 0)
  if (!Number.isFinite(dur) || dur <= 0 || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 'missing_meta'
  if (dur > 300) return 'too_long'
  const maxSide = Math.max(w, h)
  const minSide = Math.min(w, h)
  if (maxSide > 1968 || minSide > 1088) return 'too_large_res'
  return ''
}

function resetVideo() {
  videoFile.value = null
  videoUrl.value = ''
  originObjectKey.value = ''
  demoPlayed.value = false
  videoW.value = 0
  videoH.value = 0
  videoDuration.value = 0
  maskTime.value = null
  maskHasContent.value = false
  timeStart.value = 0
  timeStop.value = 0
  seekTime.value = 0
  if (seekRaf) {
    try {
      cancelAnimationFrame(seekRaf)
    } catch {}
    seekRaf = 0
  }
  clearMask()
}

function clearMask() {
  const c = maskCanvas.value
  if (!c) return
  const ctx = c.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, c.width, c.height)
  maskTime.value = null
  maskHasContent.value = false
}

function isMaskBlank() {
  const c = maskCanvas.value
  if (!c) return true
  const ctx = c.getContext('2d')
  if (!ctx) return true
  const w = Math.max(1, Math.round(Number(c.width || 0)) || 0)
  const h = Math.max(1, Math.round(Number(c.height || 0)) || 0)
  if (w <= 0 || h <= 0) return true
  const sw = Math.min(256, w)
  const sh = Math.min(256, h)
  const tmp = document.createElement('canvas')
  tmp.width = sw
  tmp.height = sh
  const tctx = tmp.getContext('2d')
  if (!tctx) return true
  tctx.clearRect(0, 0, sw, sh)
  tctx.drawImage(c, 0, 0, w, h, 0, 0, sw, sh)
  const img = tctx.getImageData(0, 0, sw, sh)
  for (let i = 3; i < img.data.length; i += 4) {
    if (Number(img.data[i] || 0) > 0) return false
  }
  return true
}

function ensureMaskCanvasSize() {
  const c = maskCanvas.value
  if (!c) return
  const rect = c.getBoundingClientRect()
  const cssW = Math.max(1, rect.width)
  const cssH = Math.max(1, rect.height)
  const dpr = Math.min(2, Math.max(1, Number((window as any)?.devicePixelRatio || 1)))
  const w = Math.max(1, Math.round(cssW * dpr))
  const h = Math.max(1, Math.round(cssH * dpr))
  if (c.width === w && c.height === h) return
  const prev = document.createElement('canvas')
  prev.width = Math.max(1, c.width || 1)
  prev.height = Math.max(1, c.height || 1)
  const prevCtx = prev.getContext('2d')
  if (prevCtx) prevCtx.drawImage(c, 0, 0)
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, w, h)
  if (prevCtx) ctx.drawImage(prev, 0, 0, prev.width, prev.height, 0, 0, w, h)
}

function getVideoContentBoxInCanvasPx(canvasRect: DOMRect, canvasEl: HTMLCanvasElement) {
  const vw = Math.max(0, Math.round(Number(videoEl.value?.videoWidth || 0)))
  const vh = Math.max(0, Math.round(Number(videoEl.value?.videoHeight || 0)))
  if (!vw || !vh) return null
  const rw = Math.max(1, canvasRect.width)
  const rh = Math.max(1, canvasRect.height)
  const scale = Math.min(rw / vw, rh / vh)
  const cssW = vw * scale
  const cssH = vh * scale
  const cssX = (rw - cssW) / 2
  const cssY = (rh - cssH) / 2
  const scaleX = canvasEl.width / rw
  const scaleY = canvasEl.height / rh
  return {
    x: cssX * scaleX,
    y: cssY * scaleY,
    w: cssW * scaleX,
    h: cssH * scaleY,
  }
}

function pointerToCanvasPoint(e: PointerEvent) {
  const c = maskCanvas.value
  if (!c) return null
  ensureMaskCanvasSize()
  const rect = c.getBoundingClientRect()
  const box = getVideoContentBoxInCanvasPx(rect, c)
  if (!box) {
    outOfBounds.value = true
    brushCursorVisible.value = false
    return null
  }
  const scaleX = c.width / Math.max(1, rect.width)
  const scaleY = c.height / Math.max(1, rect.height)
  brushCursorScale.value = (scaleX + scaleY) / 2
  const x = (e.clientX - rect.left) * scaleX
  const y = (e.clientY - rect.top) * scaleY
  const inside = x >= box.x && y >= box.y && x <= box.x + box.w && y <= box.y + box.h
  outOfBounds.value = !inside
  if (!inside) {
    brushCursorVisible.value = false
    return null
  }
  if (hasPreviewVideo.value && e.pointerType !== 'touch') {
    brushCursorVisible.value = true
    brushCursorX.value = x / scaleX
    brushCursorY.value = y / scaleY
  } else {
    brushCursorVisible.value = false
  }
  return { x, y }
}

function drawLine(from: { x: number; y: number }, to: { x: number; y: number }) {
  const c = maskCanvas.value
  if (!c) return
  const ctx = c.getContext('2d')
  if (!ctx) return
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.lineWidth = Math.max(4, Math.round(Number(brushSize.value || 0)) || 28)
  if (toolMode.value === 'eraser') {
    ctx.globalCompositeOperation = 'destination-out'
    ctx.strokeStyle = 'rgba(0,0,0,1)'
  } else {
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = 'rgba(0,139,255,1)'
  }
  ctx.beginPath()
  ctx.moveTo(from.x, from.y)
  ctx.lineTo(to.x, to.y)
  ctx.stroke()
  ctx.restore()
  if (toolMode.value === 'brush') maskHasContent.value = true
}

function onMaskPointerDown(e: PointerEvent) {
  if (!videoFile.value) return
  outOfBounds.value = false
  ensureMaskCanvasSize()
  const pt = pointerToCanvasPoint(e)
  if (!pt) return
  try {
    ;(e.target as HTMLElement)?.setPointerCapture?.(e.pointerId)
  } catch {}
  drawing.value = true
  lastPt.value = pt
  const ct = Math.max(0, Number(videoEl.value?.currentTime || 0))
  const mt = Math.round(ct * 1000) / 1000
  if (eraseStrategy.value === 'track') {
    if (maskTime.value !== null && Math.abs(maskTime.value - mt) > 0.05) clearMask()
    maskTime.value = mt
  }
  seekTime.value = ct
  drawLine(pt, pt)
}

function onMaskPointerMove(e: PointerEvent) {
  const pt = pointerToCanvasPoint(e)
  if (!drawing.value) return
  const last = lastPt.value
  if (!pt) {
    lastPt.value = null
    return
  }
  if (!last) {
    lastPt.value = pt
    drawLine(pt, pt)
    return
  }
  drawLine(last, pt)
  lastPt.value = pt
}

function onMaskPointerUp() {
  drawing.value = false
  lastPt.value = null
  outOfBounds.value = false
  brushCursorVisible.value = false
  maskHasContent.value = !isMaskBlank()
}

async function createThumb(): Promise<string> {
  const vid = videoEl.value
  if (!vid) return ''
  const c = document.createElement('canvas')
  const w = Math.max(1, vid.videoWidth || videoW.value || 1)
  const h = Math.max(1, vid.videoHeight || videoH.value || 1)
  const scale = Math.min(1, 320 / w)
  c.width = Math.round(w * scale)
  c.height = Math.round(h * scale)
  const ctx = c.getContext('2d')
  if (!ctx) return ''
  ctx.drawImage(vid, 0, 0, c.width, c.height)
  return c.toDataURL('image/jpeg', 0.72)
}

function toMaskPngBase64() {
  const c = maskCanvas.value
  if (!c) return ''
  try {
    const vw = Math.max(0, Math.round(Number(videoEl.value?.videoWidth || 0)))
    const vh = Math.max(0, Math.round(Number(videoEl.value?.videoHeight || 0)))
    if (!vw || !vh) return ''
    const rect = c.getBoundingClientRect()
    const box = getVideoContentBoxInCanvasPx(rect, c)
    if (!box) return ''

    const scaled = document.createElement('canvas')
    scaled.width = vw
    scaled.height = vh
    const scaledCtx = scaled.getContext('2d')
    if (!scaledCtx) return ''
    scaledCtx.clearRect(0, 0, vw, vh)
    scaledCtx.drawImage(c, box.x, box.y, box.w, box.h, 0, 0, vw, vh)

    const src = scaledCtx.getImageData(0, 0, vw, vh)
    const dst = scaledCtx.createImageData(vw, vh)
    for (let i = 0; i < src.data.length; i += 4) {
      const a = Number(src.data[i + 3] || 0)
      if (a > 0) {
        dst.data[i] = 255
        dst.data[i + 1] = 255
        dst.data[i + 2] = 255
        dst.data[i + 3] = 255
      }
    }
    scaledCtx.putImageData(dst, 0, 0)
    return normalizePngMaskBase64(scaled.toDataURL('image/png'))
  } catch {
    return ''
  }
}

async function openFilePicker() {
  trackEvent('landing_upload_click', { landing: 'video-object-removal' })
  await nextTick()
  filePicker.value?.click()
}

function onDropFile(e: DragEvent) {
  const dt = e.dataTransfer
  const f = dt && dt.files && dt.files[0] ? dt.files[0] : null
  if (!f) return
  const name = String(f.name || '').toLowerCase()
  const ext = name.includes('.') ? name.split('.').pop() || '' : ''
  const isVideo = String(f.type || '').startsWith('video/')
  const isAllowed = isVideo || ext === 'mp4' || ext === 'mov'
  if (!isAllowed) {
    openWarn('仅支持MP4和MOV视频格式')
    return
  }
  trackEvent('landing_upload_drop', { landing: 'video-object-removal' })
  const url = URL.createObjectURL(f)
  resetVideo()
  videoFile.value = f
  videoUrl.value = url
  demoPlayed.value = true
  void nextTick().then(() => {
    ensureMaskCanvasSize()
    clearMask()
  })
}

function openHowTo() {
  const url = 'https://suiyu-network.feishu.cn/wiki/FtpvwylhQiqWNAklbeMcWls1n7b?larkTabName=space#share-EVsidcbYboOtIsx8nafc9vpQnig'
  try {
    const w = window.open(url, '_blank', 'noopener,noreferrer')
    if (w) w.opener = null
  } catch {
    window.open(url, '_blank')
  }
}

function onFilePicked(e: Event) {
  const input = e.target as HTMLInputElement
  const f = input.files && input.files[0] ? input.files[0] : null
  if (input) input.value = ''
  if (!f) return
  const url = URL.createObjectURL(f)
  resetVideo()
  videoFile.value = f
  videoUrl.value = url
  demoPlayed.value = true
  void nextTick().then(() => {
    ensureMaskCanvasSize()
    clearMask()
  })
}

async function playDemoVideo() {
  if (demoPlayed.value) return
  if (videoFile.value) return
  if (videoUrl.value) return
  const demo = String(props.demoVideoUrl || '').trim()
  if (!demo) return
  demoPlayed.value = true
  videoUrl.value = demo
  await nextTick()
  const v = videoEl.value
  if (!v) return
  try {
    v.muted = true
    await v.play()
  } catch {}
}

function onVideoLoadedMeta() {
  const vid = videoEl.value
  if (!vid) return
  videoW.value = Math.round(vid.videoWidth || 0)
  videoH.value = Math.round(vid.videoHeight || 0)
  videoDuration.value = Number(vid.duration || 0)
  timeStart.value = 0
  timeStop.value = Math.max(0, Number(vid.duration || 0))
  playerPlaying.value = !vid.paused && !vid.ended
  try {
    vid.muted = true
    if (Number(vid.volume ?? 1) <= 0.0001) vid.volume = 1
  } catch {}
  playerMuted.value = Boolean(vid.muted)
  playerLastVolume.value = Math.max(0, Math.min(1, Number(vid.volume ?? 1)))
  seekTime.value = Math.max(0, Number(vid.currentTime || 0))
  ensureMaskCanvasSize()
}

function startSeekSync() {
  if (seekRaf) return
  const tick = () => {
    const vid = videoEl.value
    if (!vid) {
      seekRaf = 0
      return
    }
    if (playerPlaying.value) {
      if (!seekDragging.value) seekTime.value = Math.max(0, Number(vid.currentTime || 0))
      seekRaf = requestAnimationFrame(tick)
      return
    }
    seekRaf = 0
  }
  seekRaf = requestAnimationFrame(tick)
}

function stopSeekSync() {
  if (!seekRaf) return
  try {
    cancelAnimationFrame(seekRaf)
  } catch {}
  seekRaf = 0
}

function syncPlayerState() {
  const vid = videoEl.value
  if (!vid) return
  playerPlaying.value = !vid.paused && !vid.ended
  playerMuted.value = Boolean(vid.muted)
  const v = Math.max(0, Math.min(1, Number(vid.volume ?? 1)))
  if (v > 0.0001) playerLastVolume.value = v
  if (playerPlaying.value) startSeekSync()
  else stopSeekSync()
}

function onVideoEnded() {
  syncPlayerState()
  if (!isDemoVideo.value) return
  const v = videoEl.value
  if (!v) return
  if (!isMobileViewport.value) {
    try {
      v.pause()
      v.currentTime = 0
    } catch {}
    videoUrl.value = ''
    syncPlayerState()
    return
  }
  try {
    v.currentTime = 0
    void v.play()
  } catch {}
}

function openWeChatMiniProgram() {
  const url = 'weixin://dl/business/?appid=wx34b5be765618750e&path=pages/index/index&env_version=release'
  try {
    window.location.href = url
  } catch {}
}

async function togglePlayPause() {
  const vid = videoEl.value
  if (!vid) return
  try {
    if (vid.paused || vid.ended) await vid.play()
    else vid.pause()
  } catch {}
  syncPlayerState()
}

function toggleMute() {
  const vid = videoEl.value
  if (!vid) return
  try {
    const next = !vid.muted
    vid.muted = next
    if (!next) {
      const v = Math.max(0.2, Math.min(1, Number(playerLastVolume.value || 1)))
      vid.volume = v
    }
  } catch {}
  syncPlayerState()
}

function seekTo(t: number) {
  const vid = videoEl.value
  if (!vid) return
  const dur = Number(vid.duration || videoDuration.value || 0)
  if (!Number.isFinite(dur) || dur <= 0) return
  const next = Math.max(0, Math.min(dur, Number(t || 0)))
  const wasPaused = Boolean(vid.paused || vid.ended)
  try {
    if (Math.abs(Number(vid.currentTime || 0) - next) > 0.02) vid.currentTime = next
  } catch {}
  if (!wasPaused) {
    try {
      void vid.play()
    } catch {}
  }
  seekTime.value = next
}

watch(
  () => videoUrl.value,
  (v, old) => {
    if (old && old.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(old)
      } catch {}
    }
    if (!v) return
    void nextTick().then(() => {
      ensureMaskCanvasSize()
      clearMask()
    })
  },
)

watch(
  () => eraseStrategy.value,
  (v) => {
    if (v !== 'track') return
    if (!maskHasContent.value) return
    if (maskTime.value !== null) return
    const ct = Math.max(0, Number(videoEl.value?.currentTime || seekTime.value || 0))
    maskTime.value = Math.round(ct * 1000) / 1000
  },
)

function stopTaskPolling() {
  if (!taskPollTimer) return
  try {
    window.clearInterval(taskPollTimer)
  } catch {}
  taskPollTimer = 0
  taskPollBusy = false
  try {
    taskStore.stopPolling()
  } catch {}
}

function hasActiveTasksInList() {
  return tasks.value.some((t: any) => {
    const s = normalizeTaskStatus(t?.status)
    return s === TASK_STATUS.queued || s === TASK_STATUS.processing
  })
}

async function pollTaskDetailsOnce() {
  if (taskPollBusy) return
  if (!auth.isLoggedIn || !auth.token) {
    stopTaskPolling()
    return
  }
  const activeIds = tasks.value
    .filter((t: any) => {
      const s = normalizeTaskStatus(t?.status)
      return s === TASK_STATUS.queued || s === TASK_STATUS.processing
    })
    .map((t: any) => String(t?.task_id || '').trim())
    .filter(Boolean)
  if (!activeIds.length) {
    stopTaskPolling()
    return
  }
  taskPollBusy = true
  try {
    await Promise.allSettled(activeIds.map((id) => videoTasks.refreshOne(auth.token as string, id)))
  } finally {
    taskPollBusy = false
  }
  if (!hasActiveTasksInList()) stopTaskPolling()
}

function startTaskPollingIfNeeded() {
  if (taskPollTimer) return
  if (!hasActiveTasksInList()) return
  taskPollTimer = window.setInterval(() => {
    void pollTaskDetailsOnce()
  }, 15000)
  void pollTaskDetailsOnce()
}

async function refreshTasks() {
  if (!auth.token) return

  stopTaskPolling()
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPES.eraseObject_vid, videoTasks.listPageIndex, 'ms', undefined, true, {
      authExpiredMode: 'silent',
    })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }

  startTaskPollingIfNeeded()
}

const showPager = computed(() => videoTasks.listTotalPages > 1)
const canPrevPage = computed(() => videoTasks.listPageIndex > 0)
const canNextPage = computed(() => videoTasks.listPageIndex + 1 < videoTasks.listTotalPages)
const pageText = computed(() => `${videoTasks.listPageIndex + 1}/${videoTasks.listTotalPages}`)

async function gotoPage(idx: number) {
  if (!auth.token) return

  const i = Math.max(0, Math.round(Number(idx || 0)) || 0)
  if (i === videoTasks.listPageIndex) return
  stopTaskPolling()
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPES.eraseObject_vid, i, 'ms', undefined, true, { authExpiredMode: 'silent' })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }

  startTaskPollingIfNeeded()
}

function prevPage() {
  void gotoPage(videoTasks.listPageIndex - 1)
}

function nextPage() {
  void gotoPage(videoTasks.listPageIndex + 1)
}

async function removeTask(taskId: string) {
  if (!auth.isLoggedIn || !auth.token) {
    videoTasks.remove(taskId)
    removeTaskThumbCache(taskId)
    return
  }
  try {
    await apiRequest<any>('/task/delete', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
  } catch {}
  videoTasks.remove(taskId)
  removeTaskThumbCache(taskId)
}

function statusKey(v: any) {
  return normalizeTaskStatus(String(v || '').trim())
}

async function startExistingTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  let startResp: any
  try {
    startResp = await apiRequest<StartTaskResponse>('/task/start', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
  } catch (e) {
    if (isAuthRequiredError(e)) return
    throw e
  }
  const startCode = Number(startResp.code)
  if (startCode === 3001 || startCode === 3002) {
    const required = typeof startResp.data?.credits_required === 'number' ? startResp.data.credits_required : 0
    void paymentModal.pay({ entry: 'insufficient', creditsRequired: required, tipText: String(startResp.message || '').trim(), from: 'video-object-removal' })
    return
  }
  if (startCode !== 0) {
    openModal('启动失败', '我知道了', `<div class="miniCard"><h4>任务启动失败</h4><p>请稍后重试，或联系客服处理。</p></div>`)
    return
  }
  const st = statusKey(startResp.data?.status) || TASK_STATUS.queued
  videoTasks.upsert({ task_id: taskId, status: st as any } as any)
  taskStore.setCurrent({ task_id: taskId, status: st } as any)
  void refreshTasks()
}

async function cancelTask(taskId: string) {
  if (!auth.isLoggedIn || !auth.token) return
  try {
    await apiRequest<any>('/task/stop', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
  } catch {}
  videoTasks.upsert({ task_id: taskId, status: TASK_STATUS.paused as any } as any)
  void refreshTasks()
}

function sanitizeFilename(v: unknown) {
  const s = String(v || '').trim()
  const cleaned = s.replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_').replace(/\s+/g, ' ').trim()
  return cleaned || '视频消除'
}

function extFromUrl(u: string) {
  try {
    const url = new URL(u, window.location.href)
    const p = url.pathname.split('/').pop() || ''
    const m = p.match(/\.([a-z0-9]{1,5})$/i)
    if (!m) return ''
    return `.${String(m[1] || '').toLowerCase()}`
  } catch {
    return ''
  }
}

function rfc5987Encode(v: string) {
  return encodeURIComponent(v).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
}

function withContentDisposition(url: string, filename: string) {
  const href = String(url || '').trim()
  const name = String(filename || '').trim()
  if (!href || !name) return href
  try {
    const u = new URL(href)
    const disp = `attachment; filename*=UTF-8''${rfc5987Encode(name)}`
    u.searchParams.set('response-content-disposition', disp)
    return u.toString()
  } catch {
    return href
  }
}

function triggerBrowserDownload(url: string, filename: string) {
  const href = String(url || '').trim()
  if (!href) return
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

async function downloadUrlAs(url: string, filename: string) {
  const name = sanitizeFilename(filename)
  const ext = extFromUrl(url)
  const finalName = ext && !name.toLowerCase().endsWith(ext) ? `${name}${ext}` : name
  try {
    const href = withContentDisposition(url, finalName)
    triggerBrowserDownload(href, finalName)
  } catch {}
}

async function downloadTask(t: any) {
  if (!auth.isLoggedIn || !auth.token) return
  const taskId = String(t?.task_id || t || '').trim()
  if (!taskId) return
  const defaultName = sanitizeFilename(String(t?.task_name || t?.taskName || t?.title || t?.name || '视频消除'))
  let url = ''
  try {
    const resp = await apiRequest<any>(`/task/${encodeURIComponent(taskId)}`, { method: 'GET', token: auth.token } as any)
    const data = assertOk(resp, '获取任务失败') as any
    url = String(data.download_url || data.data?.download_url || '').trim()
  } catch (e) {
    if (isAuthRequiredError(e)) return
    openModal('下载失败', '我知道了', `<div class="miniCard"><h4>无法获取下载链接</h4><p>请稍后重试。</p></div>`)
    return
  }
  if (!url) {
    openModal('下载失败', '我知道了', `<div class="miniCard"><h4>下载链接为空</h4><p>请稍后重试。</p></div>`)
    return
  }
  await downloadUrlAs(url, defaultName)
  trackEvent('landing_export_done', { landing: 'video-object-removal', task_id: taskId })
}

async function submit() {
  if (!ensureLoggedIn()) return
  
  if (!videoFile.value) return

  if (isMaskBlank()) {
    openWarn('未发现消除区。请在视频预览区找到并用鼠标涂抹待消除的内容，再尝试提交。')
    return
  }

  const metaErr = validateVideoMeta()
  if (metaErr) {
    if (metaErr === 'missing_meta') {
      openWarn('无法读取视频信息，为保证处理效果，请重新选择视频后再提交。')
      return
    }
    if (metaErr === 'too_large_res') {
      const w = Math.round(Number(videoW.value || 0))
      const h = Math.round(Number(videoH.value || 0))
      openWarn(`视频消除笔最大支持1080P的视频分辨率。当前视频分辨率 ${w} * ${h}，请通过视频剪辑软件将分辨率降低到1080P后再提交处理。`)
      return
    }
    if (metaErr === 'too_long') {
      const dur = Math.max(0, Number(videoDuration.value || 0))
      const ss = String(Math.round(dur))
      openWarn(`视频消除笔最大支持处理5分钟内的视频。当前视频时长${ss}秒，请将其裁切到300秒以内再提交。`)
      return
    }
    openWarn('参数不合法，请重新选择视频后再提交。')
    return
  }
  
  const customMask = toMaskPngBase64()
  if (!customMask) {
    openWarn('消除区遮罩生成失败，请重新涂抹后再提交。如果此问题持续发生，请联系客服协助。')
    return
  }

  openSubmitDialog()
  uploadBusy.value = true
  try {
    if (!originObjectKey.value) {
      let upload: any
      try {
        upload = await getUploadUrl(auth.token, videoFile.value)
      } catch (e) {
        if (isAuthRequiredError(e)) {
          return
        }
        setSubmitError('upload', e instanceof Error ? e.message : '视频上传失败，请重新提交。')
        return
      }
      submitStage.value = 'uploading'
      submitUploadPct.value = 0
      try {
        await putFile(upload, videoFile.value, (p) => {
          submitUploadPct.value = p.percent
        })
      } catch {
        setSubmitError('upload', '视频上传失败，请重新提交。')
        return
      }
      originObjectKey.value = upload.object_key || ''
    }
    if (!originObjectKey.value) throw new Error('上传成功但缺少 object_key')
    submitUploadPct.value = 100

    submitStage.value = 'creating'

    const timeMaskSec =
      eraseStrategy.value === 'fixed'
        ? timeRangeReady.value
          ? Math.max(0, Math.min(Math.max(0, Number(videoDuration.value || 0)), Number(timeStart.value || 0)))
          : 0
        : Math.max(0, Number(maskTime.value || 0))

    const payload: any = {
      source_video: originObjectKey.value,
      time_mask: Math.max(0, Math.round(timeMaskSec * 1000)),
      custom_mask: customMask,
    }
    payload.track_mode = trackMode.value
    if (timeRangeReady.value) {
      const d = Math.max(0, Number(videoDuration.value || 0))
      const a = Math.max(0, Math.min(d, Number(timeStart.value || 0)))
      const b = Math.max(0, Math.min(d, Number(timeStop.value || 0)))
      if (Number.isFinite(a) && Number.isFinite(b) && b > a && (a > 0.01 || b < d - 0.01)) {
        payload.time_start = Math.max(0, Math.round(a * 1000))
        payload.time_stop = Math.max(0, Math.round(b * 1000))
      }
    }
    const rawName = String(videoFile.value.name || '').trim()
    const taskName = rawName.replace(/\.[^./\\]+$/, '')
    if (taskName) payload.task_name = taskName

    let taskId = ''
    let createdStatus = 'created'
    let creditsRequired = 0
    try {
      const resp = await apiRequest<VideoEraseResponse>('/video/erase', {
        method: 'POST',
        token: auth.token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      } as any)
      const data = assertOk(resp, '提交失败') as any
      taskId = String(data.task_id || '').trim()
      createdStatus = String(data.status || 'created').toLowerCase()
      creditsRequired = typeof data.credits_required === 'number' ? data.credits_required : 0
      if (!taskId) throw new Error('empty task_id')
    } catch (e) {
      if (isAuthRequiredError(e)) return
      setSubmitError('create', '任务创建失败，请重新提交或联系客服处理。')
      return
    }

    taskStore.setCurrent({ task_id: taskId, status: 'created' } as any)
    const thumb = await createThumb()
    if (thumb) setTaskThumbToCache(taskId, thumb)
    const localDesc = `${eraseStrategy.value === 'fixed' ? '固定位置' : '智能跟踪'}${timeRangeActive.value ? ' · 时间区间' : ''}`
    videoTasks.upsert({
      task_id: taskId,
      title: videoFile.value.name || '视频消除笔',
      created_at: Date.now(),
      status: createdStatus as any,
      task_type: TASK_TYPES.eraseObject_vid,
      description: localDesc,
      source_video: originObjectKey.value,
      thumb_data_url: thumb || undefined,
    })
    try {
      await videoTasks.fetchRecent(auth.token, 1, TASK_TYPES.eraseObject_vid, 0, 'ms', undefined, true, { authExpiredMode: 'silent' })
    } catch (e) {
      if (isAuthRequiredError(e)) videoTasks.reset()
    }

    submitStage.value = 'starting'
    let startResp: any
    try {
      startResp = await apiRequest<StartTaskResponse>('/task/start', {
        method: 'POST',
        token: auth.token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId }),
      } as any)
    } catch (e) {
      if (isAuthRequiredError(e)) return
      throw e
    }
    const startCode = Number(startResp.code)
    if (startCode === 3001 || startCode === 3002) {
      const required = typeof startResp.data?.credits_required === 'number' ? startResp.data.credits_required : creditsRequired
      closeSubmitDialog()
      void paymentModal.pay({ entry: 'insufficient', creditsRequired: required, tipText: String(startResp.message || '').trim(), from: 'video-object-removal' })
      return
    }
    if (startCode !== 0) {
      setSubmitError('start', '任务启动失败，请重试')
      return
    }
    const st = statusKey(startResp.data?.status) || TASK_STATUS.queued
    videoTasks.upsert({ task_id: taskId, status: st as any } as any)
    taskStore.setCurrent({ task_id: taskId, status: st } as any)

    try {
      await auth.fetchMe()
    } catch {}
    resetVideo()
    void refreshTasks()
    closeSubmitDialog()
  } catch (e) {
    if (isAuthRequiredError(e)) return
    setSubmitError('create', e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
  }
}

function onEscape(e: KeyboardEvent) {
  if (e.key !== 'Escape') return
  if (modalOpen.value) closeModal()
}

onMounted(() => {
  window.addEventListener('keydown', onEscape)
  window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  try {
    syncMobileViewport()
    window.addEventListener('resize', syncMobileViewport)
  } catch {}
  
  try {
    const el = previewWrapEl.value
    if (el && typeof ResizeObserver !== 'undefined') {
      maskResizeObs = new ResizeObserver(() => {
        ensureMaskCanvasSize()
      })
      maskResizeObs.observe(el)
    }
  } catch {}

  videoTasks.init()
  void refreshTasks()

  void playDemoVideo()
  
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onEscape)
  stopSeekSync()
  stopTaskPolling()
  try {
    window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  } catch {}
  try {
    window.removeEventListener('resize', syncMobileViewport)
  } catch {}
  try {
    maskResizeObs?.disconnect()
  } catch {}
  maskResizeObs = null
})

onBeforeRouteLeave(() => {
  stopSeekSync()
  stopTaskPolling()
})

watch(
  () => auth.isLoggedIn,
  (v) => {
    if (v && auth.token) void refreshTasks()
  },
)

const tasks = computed(() => {
  if (!auth.isLoggedIn) return []
  return videoTasks.items.filter((x: any) => x.task_type === TASK_TYPES.eraseObject_vid && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
})
const hasTasks = computed(() => tasks.value && tasks.value.length > 0)

const trackMode = computed(() => (eraseStrategy.value === 'fixed' ? 'no_tracking' : 'auto_tracking'))
const submitPriceText = computed(() => getBatchCreditsStandardText(TASK_TYPES.eraseObject_vid, trackMode.value))
const timeRangeReady = computed(() => Math.max(0, Number(videoDuration.value || 0)) > 0)
const timeRangeActive = computed(() => {
  const d = Math.max(0, Number(videoDuration.value || 0))
  if (!d) return false
  const s = Math.max(0, Math.min(d, Number(timeStart.value || 0)))
  const e = Math.max(0, Math.min(d, Number(timeStop.value || 0)))
  return s > 0.01 || e < d - 0.01
})

function previewAt(t: number) {
  const vid = videoEl.value
  if (!vid) return
  const dur = Number(vid.duration || videoDuration.value || 0)
  if (!Number.isFinite(dur) || dur <= 0) return
  const next = Math.max(0, Math.min(dur, Number(t || 0)))
  try {
    vid.pause()
  } catch {}
  try {
    if (Math.abs(Number(vid.currentTime || 0) - next) > 0.02) vid.currentTime = next
  } catch {}
}

watch(
  () => [videoDuration.value],
  () => {
    const d = Math.max(0, Number(videoDuration.value || 0))
    if (!d) return
    const gap = 3
    let s = Math.max(0, Math.min(d, Number(timeStart.value || 0)))
    let e = Math.max(0, Math.min(d, Number(timeStop.value || 0)))
    if (e - s < gap) {
      if (s + gap <= d) e = s + gap
      else {
        e = d
        s = Math.max(0, d - gap)
      }
      timeStart.value = Math.round(s * 1000) / 1000
      timeStop.value = Math.round(e * 1000) / 1000
    }
  },
)

function fmtDuration(sec: any) {
  const n = Math.max(0, Math.round(Number(sec || 0)))
  const m = String(Math.floor(n / 60)).padStart(2, '0')
  const s = String(n % 60).padStart(2, '0')
  return `${m}:${s}`
}

function taskDurationText(t: any) {
  const metering = Number(t?.metering || 0)
  if (!Number.isFinite(metering) || metering <= 0) return ''
  const sec = metering > 1000 ? metering / 1000 : metering
  return fmtDuration(sec)
}

function taskDescText(t: any) {
  const d = String(t?.description || '').trim()
  if (d) return d
  return '视频消除笔'
}

function taskTrackMode(t: any) {
  const raw = String(t?.track_mode || t?.trackMode || t?.track || '').trim()
  if (raw) return raw
  const desc = taskDescText(t)
  if (desc.includes('智能跟踪')) return 'auto_tracking'
  return 'no_tracking'
}

function fmtMD(ms: any) {
  const n = Number(ms || 0)
  if (!Number.isFinite(n) || n <= 0) return ''
  const d = new Date(n)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${m}-${day} ${hh}:${mm}`
}

function taskProgressValue(t: any) {
  const p = Number(t?.progress ?? 0)
  if (!Number.isFinite(p)) return 0
  return Math.max(0, Math.round(p))
}

function taskEstimateMinutes(t: any) {
  const metering = Number(t?.metering ?? 0)
  const sec = metering > 1000 ? metering / 1000 : metering
  return estimateTaskMinutes({ taskType: TASK_TYPES.eraseObject_vid, meteringSeconds: sec, trackMode: taskTrackMode(t) })
}

function taskHintText(t: any) {
  const st = normalizeTaskStatus(t?.status)
  const p = taskProgressValue(t)
  if (st === TASK_STATUS.queued) {
    if (p <= 0) return '处理即将开始'
    return `前面还有${p}个任务在处理`
  }
  if (st === TASK_STATUS.processing) {
    const min = taskEstimateMinutes(t)
    if (p <= 0) return `预计${min}分钟内完成`
    return `处理进度${p}%`
  }
  return ''
}
</script>

<template>
  <LandingBackground :enabled="!props.embed">
    <div class="page" :class="{ embedded: props.embed }">
      <HomePageHeader v-if="!props.embed" />

      <main class="main">
      <div v-if="!props.embed" class="container">
        <header class="hero">
          <h1 class="heroTitle">{{ landingSeo.h1 }}</h1>
          <p v-if="heroSubText" class="heroSub">{{ heroSubText }}</p>
        </header>
      </div>

      <section class="container" aria-label="处理模块">
        <div class="tool-grid">
          <div class="card" aria-label="预览与提交">
            <div class="card-hd card-hd-no-divider !hidden sm:!flex">
              <div class="cardTitleRow">
                <button class="tutorialLink" type="button" aria-label="使用教程" @click="openHowTo">
                  <span class="tutorialIcon" aria-hidden="true" :style="tutorialIconMaskStyle"></span>
                  <span>教程</span>
                </button>
              </div>
              <div class="cardOps" aria-label="工具栏">
                <div class="brushGroup" role="group" aria-label="画笔工具与粗细">
                  <div class="seg seg-toggle segNoBg" role="group" aria-label="刷子与橡皮擦">
                    <button class="chip chipIcon" type="button" :aria-pressed="toolMode === 'brush'" aria-label="刷子" @click="toolMode = 'brush'">
                      <IconBrush />
                    </button>
                    <button class="chip chipIcon" type="button" :aria-pressed="toolMode === 'eraser'" aria-label="橡皮擦" @click="toolMode = 'eraser'">
                      <IconEraser />
                    </button>
                  </div>

                  <label class="brushSlider" aria-label="笔刷粗细">
                    <input
                      class="brushSliderInput"
                      type="range"
                      min="12"
                      max="64"
                      step="1"
                      v-model.number="brushSize"
                      aria-label="笔刷粗细滑动条"
                    />
                  </label>
                </div>

                <button class="btn mini openVideoBtn" type="button" aria-label="打开视频" @click="openFilePicker">打开视频</button>
              </div>
            </div>

            <div class="card-bd">
              <input ref="filePicker" type="file" accept="video/*" style="display: none" @change="onFilePicked" />

              <div
                ref="previewWrapEl"
                class="video-frame"
                :class="{ outOfBounds }"
                aria-label="视频预览窗口"
                @dragenter.capture.prevent
                @dragover.capture.prevent
                @drop.capture.prevent.stop="onDropFile"
              >
              <video
                v-if="videoUrl"
                ref="videoEl"
                class="videoMedia"
                :src="videoUrl"
                :muted="isDemoVideo"
                :autoplay="isDemoVideo"
                :loop="isDemoVideo && isMobileViewport"
                playsinline
                preload="metadata"
                @loadedmetadata="onVideoLoadedMeta"
                @play="syncPlayerState"
                @pause="syncPlayerState"
                @ended="onVideoEnded"
              ></video>
              <div v-else class="video-placeholder" role="button" tabindex="0" aria-label="打开视频" @click="openFilePicker" @keydown.enter.prevent="openFilePicker">
                <img class="cameraIcon" :src="defaultVideoThumbDataUrl()" alt="" aria-hidden="true" />
                <div class="video-placeholder-text">
                  <div class="video-placeholder-title">将视频拖放到此</div>
                  <div class="video-placeholder-sub">支持MP4和MOV格式，最大1080P，最长5分钟</div>
                </div>
              </div>

              <div
                v-if="brushCursorVisible"
                class="brushCursor !hidden sm:!block"
                :style="{
                  left: `${brushCursorX}px`,
                  top: `${brushCursorY}px`,
                  width: `${brushCursorDiameter}px`,
                  height: `${brushCursorDiameter}px`,
                }"
                aria-hidden="true"
              ></div>

              <canvas
                ref="maskCanvas"
                class="maskCanvas !hidden sm:!block"
                :class="{ outOfBounds, cursorCircle: brushCursorVisible }"
                :style="{ opacity: maskDisplayActive ? 0.72 : 0 }"
                aria-label="涂抹遮罩层"
                role="application"
                tabindex="0"
                @pointerdown="onMaskPointerDown"
                @pointermove="onMaskPointerMove"
                @pointerup="onMaskPointerUp"
                @pointercancel="onMaskPointerUp"
                @pointerleave="onMaskPointerUp"
              ></canvas>
            </div>

            <div v-if="hasPreviewVideo && !isDemoVideo" class="playerRow !hidden sm:!flex" aria-label="播放与时间区间选择">
              <button class="pill previewControlBtn" type="button" :aria-label="playerPlaying ? '暂停' : '播放'" @click="togglePlayPause">
                <component :is="playerPlaying ? IconPause : IconPlay" />
              </button>

              <button class="pill previewControlBtn" type="button" :aria-label="playerMuted ? '打开音量' : '静音'" @click="toggleMute">
                <component :is="playerMuted ? IconVolumeOff : IconVolume" />
              </button>

              <div class="timeRangeWrap" aria-label="时间区间选择">
                <TimeRangeSeekBar
                  :duration="Number(videoDuration || 0)"
                  v-model:start="timeStart"
                  v-model:end="timeStop"
                  :min-gap="Math.min(3, Number(videoDuration || 0) || 0)"
                  :disabled="!timeRangeReady"
                  :no-bg="true"
                  v-model:seek="seekTime"
                  :mask="eraseStrategy === 'track' ? maskTime : null"
                  @seek-dragging="seekDragging = $event"
                  @preview-trim="previewAt"
                  @preview-seek="seekTo"
                />
              </div>
            </div>

            <div v-if="isDemoVideo" class="mt-4 sm:hidden">
              <button class="btn btn-primary w-full" type="button" @click="openWeChatMiniProgram">开始万能视频消除</button>
            </div>

            <div class="video-toolbar !hidden sm:!flex" aria-label="处理方式与提交">
              <div class="modeBlock" aria-label="模式说明与切换">
                <div class="modeHint">
                  {{ eraseStrategy === 'fixed' ? '消除即梦、豆包水印，以及视频中的马赛克、贴纸等遮挡' : '消除视频中移动的物体，如走动的人，移动的汽车等' }}
                </div>
                <div class="seg seg-toggle segStrongActive" role="group" aria-label="固定位置与智能跟踪">
                  <button class="chip" type="button" :aria-pressed="eraseStrategy === 'fixed'" @click="eraseStrategy = 'fixed'">固定位置</button>
                  <button class="chip" type="button" :aria-pressed="eraseStrategy === 'track'" @click="eraseStrategy = 'track'">智能跟踪</button>
                </div>
              </div>

              <button class="btn btn-primary btn-submit" type="button" :disabled="uploadBusy || !videoFile" aria-label="提交处理" @click="submit">
                <span>提交处理</span>
                <span class="submitPrice">{{ submitPriceText }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="card !hidden sm:!block" aria-label="任务列表">
          <div class="card-hd card-hd-no-divider taskListHd">
            <div class="card-title">任务列表（近1天）</div>
            <div class="taskListHdRight" aria-label="任务列表工具栏">
              <button class="pill pillNoBg" type="button" aria-label="刷新任务列表" @click="refreshTasks">
                <IconRefresh />
              </button>
              <div v-if="showPager" class="taskPager" aria-label="任务列表分页">
                <button class="iconBtn iconBtnNoBg" type="button" :disabled="!canPrevPage" aria-label="上一页" @click="prevPage"><IconChevronLeft /></button>
                <span class="muted">{{ pageText }}</span>
                <button class="iconBtn iconBtnNoBg" type="button" :disabled="!canNextPage" aria-label="下一页" @click="nextPage"><IconChevronRight /></button>
              </div>
            </div>
          </div>

          <div class="card-bd task-list-bd">
            <div v-if="hasTasks" class="tasks" role="list">
              <div class="task" v-for="t in tasks" :key="t.task_id" role="listitem" tabindex="0">
                <div class="thumb" aria-hidden="true">
                  <img :src="taskThumbSrc(t)" alt="" aria-hidden="true" loading="lazy" />
                </div>

                <div class="task-main">
                  <div>
                    <div class="task-title">{{ t.title || '视频消除笔' }}</div>
                    <div class="task-meta">
                      <span>{{ taskDurationText(t) || '--:--' }} | {{ taskDescText(t) }} | {{ fmtMD(t.created_at) || '-' }}</span>
                    </div>
                  </div>

                  <div class="task-actions">
                    <button
                      v-if="t.status === TASK_STATUS.created || t.status === TASK_STATUS.paused || t.status === TASK_STATUS.failed"
                      class="btn btn-icon"
                      type="button"
                      aria-label="启动"
                      @click="startExistingTask(t.task_id)"
                    >
                      <IconPlay />
                    </button>
                    <button
                      v-if="t.status === TASK_STATUS.queued"
                      class="btn btn-icon"
                      type="button"
                      aria-label="中止"
                      @click="cancelTask(t.task_id)"
                    >
                      <IconClose />
                    </button>
                    <button v-if="t.status === TASK_STATUS.success" class="btn btn-icon" type="button" aria-label="下载" @click="downloadTask(t)">
                      <IconDownload />
                    </button>
                    <button
                      v-if="t.status === TASK_STATUS.created || t.status === TASK_STATUS.paused || t.status === TASK_STATUS.failed || t.status === TASK_STATUS.success"
                      class="btn btn-icon"
                      type="button"
                      aria-label="删除"
                      @click="removeTask(t.task_id)"
                    >
                      <IconTrash />
                    </button>
                  </div>
                </div>

                <div class="task-status">
                  <TaskStatusTag :status="t.status" />
                  <div v-if="taskHintText(t)" class="task-hint">{{ taskHintText(t) }}</div>
                </div>
              </div>
            </div>

            <div v-else class="miniCard" style="margin-top: 10px">
              <h4>使用说明</h4>
              <p>用鼠标涂抹视频中待消除对象。通过拖动前后时间轴，可以调整消除的时间范围。</p>
            </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="!props.embed && props.recommendations?.length" class="section" id="recommend">
        <div class="container">
          <div class="sectionCard">
            <div class="recommendGrid" aria-label="产品推荐">
              <div class="recommendCell" v-for="(r, idx) in props.recommendations" :key="idx">
                <div class="recommendIcon" aria-hidden="true">
                  <img class="recommendIconImg" :src="withBase(r.icon)" alt="" loading="lazy" />
                </div>
                <div class="recommendBody">
                  <div class="recommendName">{{ r.name }}</div>
                  <div class="recommendDesc">{{ r.desc }}</div>
                  <a class="ghost mini recommendCta" :href="resolveRecommendHref(r.href)">
                    <span>去使用</span>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <template v-if="!props.embed">
      <section v-if="sectionEnabled('howTo') && landingSeo.howto?.length" class="section" id="howto">
        <div class="container">
          <div class="sectionCard">
            <article class="seo">
              <h2>{{ landingSeo.howtoTitle || props.howToTitle }}</h2>
              <div class="howtoGrid" aria-label="使用步骤">
                <div class="howtoCell" v-for="(s, idx) in landingSeo.howto" :key="idx">
                  <div class="howtoIcon" aria-hidden="true">
                    <svg viewBox="0 0 44 44" fill="none">
                      <circle cx="22" cy="22" r="20" stroke="currentColor" stroke-width="2" opacity="0.22" />
                      <circle cx="22" cy="22" r="18" stroke="currentColor" stroke-width="2" opacity="0.10" />
                      <text x="22" y="28" text-anchor="middle" font-size="18" font-weight="800" fill="currentColor">{{ Number(idx) + 1 }}</text>
                    </svg>
                  </div>
                  <div class="howtoName">{{ s.title }}</div>
                  <div class="howtoDesc">{{ s.desc }}</div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="sectionEnabled('useCases') && landingSeo.whereTitle && landingSeo.whereItems?.length" class="section" id="where">
        <div class="container">
          <div class="sectionCard">
            <article class="seo">
              <h2>{{ landingSeo.whereTitle }}</h2>
              <div class="whereGrid" aria-label="适用场景">
                <div class="whereCell" v-for="(w, idx) in landingSeo.whereItems.slice(0, 12)" :key="idx">
                  <h3>{{ w.title }}</h3>
                  <div class="whereThumb" aria-hidden="true">
                    <img class="whereImg" :src="whereImageSrc(Number(idx))" :alt="w.title" loading="lazy" />
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="sectionEnabled('features') && landingSeo.featuresTitle && featureItems.length" class="section" id="features">
        <div class="container">
          <div class="sectionCard">
            <article class="seo">
              <h2>{{ landingSeo.featuresTitle }}</h2>
              <div class="featuresList" aria-label="功能亮点">
                <div class="featureRow" v-for="(f, idx) in featureItems" :key="idx">
                  <div class="featureMedia" aria-hidden="true">
                    <img class="featureImg" :src="withBase(f.image)" :alt="f.title" loading="lazy" />
                  </div>
                  <div class="featureBody">
                    <h3>{{ f.title }}</h3>
                    <p>{{ f.desc }}</p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="sectionEnabled('benefits') && landingSeo.whyNeedTitle && landingSeo.whyNeedItems?.length" class="section" id="why-erase">
        <div class="container">
          <div class="sectionCard">
            <article class="seo">
              <h2>{{ landingSeo.whyNeedTitle }}</h2>
              <div class="whyNeedGrid">
                <div class="whyNeedCard" v-for="(x, idx) in landingSeo.whyNeedItems" :key="idx">
                  <img class="whyNeedImg" :src="whyNeedImageSrc(Number(idx))" :alt="x.title" loading="lazy" />
                  <div class="whyNeedOverlay">
                    <h3>{{ x.title }}</h3>
                    <p>{{ x.desc }}</p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="sectionEnabled('whyChoose') && landingSeo.whyTitle && whyChooseItems.length" class="section" id="why-choose">
        <div class="container">
          <div class="sectionCard">
            <article class="seo">
              <h2>{{ landingSeo.whyTitle }}</h2>
              <div class="whyChooseGrid" aria-label="为什么选择">
                <div class="whyChooseCard" v-for="(w, idx) in whyChooseItems" :key="idx">
                  <img class="whyChooseImg" :src="withBase(w.image)" :alt="w.title" loading="lazy" />
                  <h3>{{ w.title }}</h3>
                  <p>{{ w.desc }}</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="sectionEnabled('testimonials') && landingSeo.quoteTitle && landingSeo.quoteItems?.length" class="section" id="quotes">
        <div class="container">
          <div class="sectionCard">
            <article class="seo">
              <h2>{{ landingSeo.quoteTitle }}</h2>
              <div class="letterGrid" aria-label="用户推荐信">
                <article class="letterCard" v-for="(q, idx) in landingSeo.quoteItems.slice(0, 4)" :key="idx">
                  <h3 class="letterTitle">{{ q.title }}</h3>
                  <div class="letterStars" aria-hidden="true">★★★★★</div>
                  <p class="letterText">{{ q.text }}</p>
                  <footer class="letterPerson" aria-label="推荐人信息">
                    <img class="letterAvatar" :src="withBase(`/assets/quote-avatar-${Number(idx) + 1}.jpg`)" :alt="`${q.name} 头像`" loading="lazy" />
                    <div class="letterMeta">
                      <div class="letterName">{{ q.name }}</div>
                      <div class="letterJob">{{ q.job }}</div>
                    </div>
                  </footer>
                </article>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="sectionEnabled('faq') && landingSeo.faq?.length" class="section faq" id="faq">
        <div class="container">
          <div class="sec-head">
            <h2>{{ landingSeo.faqTitle || '常见问题' }}</h2>
          </div>
          <div aria-label="常见问题">
            <details v-for="(f, idx) in landingSeo.faq" :key="idx">
              <summary>
                <span>{{ f.question }}</span>
                <span class="plus">+</span>
              </summary>
              <div class="a">{{ f.answer }}</div>
            </details>
          </div>
        </div>
      </section>

      <section v-if="sectionEnabled('contact')" class="section" id="contact">
        <div class="container" id="cta">
          <div class="cta-band">
            <h2>欢迎使用无痕AI{{ props.breadcrumbName }}</h2>
            <p>新用户赠送免费试用积分，有问题可随时联系客服</p>
            <a class="btn btn-primary" href="#">开始在线{{ props.breadcrumbName }}｜免费试用
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </a>
            <div class="cta-qr">
              <div class="qr-wrap" aria-label="客服微信二维码">
                <img :src="withBase('/assets/custom-service-qr.webp')" alt="客服微信二维码" loading="lazy" />
              </div>
              <span class="qr-tag">在线时间：09:00 - 22:00</span>
            </div>
          </div>
        </div>
      </section>
      </template>
    </main>

    <HomePageFooter v-if="!props.embed" />

    <TaskSubmitProgressDialog
      v-model:open="submitOpen"
      aria-label="视频消除笔提交进度"
      :stage="submitStage"
      :upload-pct="submitUploadPct"
      :error-step="submitErrorStep"
      :error-text="submitErrorText"
      :labels="{ upload: '上传视频', create: '创建任务', start: '启动处理' }"
      @ack="closeSubmitDialog"
    />

    <MessageDialog v-model:open="warnOpen" title="提示" :message="warnText" variant="info" primary-text="我知道了" @primary="closeWarn" @close="closeWarn" />

    <div v-if="modalOpen" class="modalOverlay" role="dialog" aria-modal="true" aria-label="提示对话框" style="display: flex">
      <div class="modal">
        <div class="modalHeader">
          <strong>{{ modalTitle }}</strong>
          <button class="iconBtn" type="button" aria-label="关闭" @click="closeModal"><IconClose /></button>
        </div>
        <div class="modalBody">
          <div v-html="modalBodyHtml"></div>
          <div class="btnRow btnRowCenter" style="margin-top: 18px">
            <button class="primary" type="button" @click="onModalPrimary">{{ modalPrimaryText }}</button>
          </div>
        </div>
      </div>
    </div>
    </div>
  </LandingBackground>
</template>

<style scoped>
.hero {
  padding-bottom: 0;
}

.heroSub {
  margin-bottom: 22px;
}

.section + .section {
  margin-top: 18px;
}

@media (max-width: 820px) {
  .section + .section {
    margin-top: 12px;
  }
}

.maskCanvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: auto;
  opacity: 0;
  transition: opacity 120ms ease;
}

.maskCanvas.cursorCircle {
  cursor: none;
}

.brushCursor {
  position: absolute;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  border: 2px solid rgba(0, 139, 255, 0.9);
  background: rgba(0, 139, 255, 0.08);
  pointer-events: none;
  z-index: 5;
}

.videoMedia {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.cardOps {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.cardTitleRow {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.tutorialLink {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
  cursor: pointer;
}

.tutorialLink:hover {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: underline;
  text-underline-offset: 4px;
}

.tutorialIcon {
  width: 16px;
  height: 16px;
  display: block;
}

.brushGroup {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
}

.chipIcon {
  padding: 8px 10px;
}

.chipIcon :deep(svg) {
  width: 18px;
  height: 18px;
  display: block;
}

.segNoBg {
  border: none;
  background: transparent;
  padding: 0;
}

.timeRangeWrap {
  flex: 1 1 320px;
  min-width: 260px;
  border: none;
  background: transparent;
  padding: 0;
}

.timeRangeWrap :deep(.trWrap) {
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.14);
}

.timeRangeWrap :deep(.trWrap.trHover) {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.timeRangeWrap :deep(.trWrap.trNoBg) {
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.14);
}

.timeRangeWrap :deep(.trBase) {
  background: rgba(255, 255, 255, 0.78);
}

.timeRangeWrap :deep(.trRange) {
  background: var(--accent);
}

.timeRangeWrap :deep(.trHandle) {
  border-color: rgba(0, 0, 0, 0.14);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
}

.timeRangeWrap :deep(.trHandle:hover) {
  border-color: rgba(0, 0, 0, 0.22);
  background: rgba(255, 255, 255, 0.98);
}

.segStrongActive :deep(.chip[aria-pressed="true"]) {
  border: 2px solid var(--accent);
  box-shadow: 0 0 0 2px rgba(0, 139, 255, 0.22);
}

.modeBlock {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

.modeHint {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.4;
  max-width: 420px;
}

.openVideoBtn {
  background: var(--accent);
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(255, 255, 255, 0.98);
}

.openVideoBtn:hover {
  filter: brightness(0.94);
}

.video-toolbar {
  margin-top: 18px;
}

.playerRow {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.previewControlBtn {
  height: 44px;
  width: 44px;
  padding: 0;
  border-radius: 12px;
  gap: 0;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.88);
  justify-content: center;
  align-items: center;
}

.previewControlBtn :deep(svg) {
  width: 20px;
  height: 20px;
  display: block;
}

.brushSlider {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.brushSliderInput {
  width: 96px;
  accent-color: var(--accent);
}
</style>
